import { useStrings } from "@1js/localization-components";
import { PromptTabs } from "@1js/pl-card-components";
import {
  Body1,
  Button,
  Image,
  makeStyles,
  Subtitle1,
} from "@fluentui/react-components";
import React from "react";
import { useRouterNavigate } from "../../hooks/useRouterNavigate";
import { IsTeamsTab } from "../../types";
import { getPromptGalleryAppParameters, navigateToApp } from "../../utils/url";
import { SubHeadingLink } from "../MosSubheading/MosSubheading.web.string";
import {
  BrowseMicrosoftPromptsButton,
  BrowsePromptsButtonLabel,
  NoAgentsPromptsHeading,
  NoAgentsPromptsSubHeading,
  NoGroupPromptsHeading,
  NoGroupPromptsSubHeading,
  NoLikedPromptsHeading,
  NoLikedPromptsSubHeading,
  NoSavedPromptsHeading,
  NoSavedPromptsSubHeading,
  NoWorkplacePromptsHeading,
  NoWorkplacePromptsSubHeading,
} from "./EmptyStatePromptResults.string";

export const useStyles = makeStyles({
  root: {
    margin: "auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    paddingTop: "90px",
    gap: "9px",
    width: "100%",
    maxWidth: "536px",
    "@media (max-width: 600px) and (min-width: 361px)": {
      maxWidth: "350px",
    },
    "@media (max-width: 360px)": {
      maxWidth: "250px",
    },
  },
  button: {
    width: "280px",
    height: "32px",
  },
  buttonContainer: {
    paddingTop: "28px",
    textAlign: "center",
  },
  heading: {
    fontFamily: "Segoe UI",
    fontSize: "24px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "32px",
  },
  subheading: {
    fontFamily: "Segoe UI",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 400,
    lineHeight: "20px",
  },
});

type EmptyStatePromptResultsProps = {
  navItem: string;
};

export const EmptyStatePromptResults: React.FC<
  EmptyStatePromptResultsProps
> = ({ navItem }) => {
  const isTeamsTab = localStorage.getItem(IsTeamsTab) === "true" ? true : false;
  const styles = useStyles();
  const navigate = useRouterNavigate();
  const localizations = useStrings({
    NoAgentsPromptsHeading,
    NoAgentsPromptsSubHeading,
    BrowseMicrosoftPromptsButton,
    NoGroupPromptsHeading,
    NoGroupPromptsSubHeading,
    NoLikedPromptsHeading,
    NoLikedPromptsSubHeading,
    NoSavedPromptsHeading,
    NoSavedPromptsSubHeading,
    BrowsePromptsButtonLabel,
    NoWorkplacePromptsHeading,
    NoWorkplacePromptsSubHeading,
    SubHeadingLink,
  });

  const getNoResultHeading = () => {
    switch (navItem) {
      case PromptTabs.AgentPrompts:
        return localizations.NoAgentsPromptsHeading();
      case PromptTabs.GroupPrompts:
        return localizations.NoGroupPromptsHeading();
      case PromptTabs.SavedPrompts:
        return localizations.NoSavedPromptsHeading();
      case PromptTabs.LikedPrompts:
        return localizations.NoLikedPromptsHeading();
      case PromptTabs.WorkplacePrompts:
        return localizations.NoWorkplacePromptsHeading();
      default:
        return "";
    }
  };

  const getNoResultSubHeading = () => {
    switch (navItem) {
      case PromptTabs.AgentPrompts:
        return localizations.NoAgentsPromptsSubHeading();
      case PromptTabs.GroupPrompts:
        return localizations.NoGroupPromptsSubHeading();
      case PromptTabs.SavedPrompts:
        return localizations.NoSavedPromptsSubHeading();
      case PromptTabs.LikedPrompts:
        return localizations.NoLikedPromptsSubHeading();
      case PromptTabs.WorkplacePrompts:
        return localizations.NoWorkplacePromptsSubHeading();
      default:
        return "";
    }
  };

  const handleClick = () => {
    navigate(`../all`);
  };

  const navigateToPromptGalleryApp = () => {
    navigateToApp(getPromptGalleryAppParameters());
  };

  return (
    <div className={styles.root}>
      <Subtitle1 className={styles.heading} id="no-results-heading">
        {getNoResultHeading()}
      </Subtitle1>
      <Image
        src="https://copilot.cloud-dev.microsoft/images/EmptyStateCollection.webp"
        aria-labelledby="no-results-heading"
      />
      <Body1>
        <div className={styles.subheading}>{getNoResultSubHeading()}</div>
        <div className={styles.buttonContainer}>
          {!isTeamsTab && (
            <Button
              appearance="primary"
              className={styles.button}
              onClick={handleClick}
              id="button.AllPrompts"
            >
              {localizations.BrowsePromptsButtonLabel()}
            </Button>
          )}

          {isTeamsTab && (
            <Button
              appearance="primary"
              className={styles.button}
              onClick={navigateToPromptGalleryApp}
              id={"link-to-app-from-teams-channel"}
            >
              {localizations.SubHeadingLink()}
            </Button>
          )}
        </div>
      </Body1>
    </div>
  );
};
